import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import Box from '@nubank/nuds-web/components/Box/Box';
import Button from '@nubank/nuds-web/components/Button/Button';
import TextField from '@nubank/nuds-web/components/TextField/TextField';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Drawer from '@nubank/nuds-web/components/Drawer/Drawer';

import NuDSFormBR from 'components/NuDSFormBR/NuDSFormBR';
import SkeletonForTypography from 'components/SkeletonForTypography/SkeletonForTypography';
import { getProspectType, MULTI_PRODUCT } from 'utils/prospectTypes';
import { trackShortFormSubmission, trackFieldFilling } from 'tracking/application/form';

import DrawerPortal from './DrawerPortal';
import { getFieldProperties, normalizeFieldValidations } from './utils';
import { FormContent } from './styles/Form';
import CompleteFormVerticalLoading from './components/CompleteFormVertical/CompleteFormVerticalLoading';

const AsyncCompleteFormVertical = dynamic(
  import('./components/CompleteFormVertical/CompleteFormVertical'),
  {
    loading: CompleteFormVerticalLoading,
    ssr: false,
  },
);

function renderDrawerPortalComponent({
  isDrawerOpen,
  handleDrawerClose,
  completeFormTitle,
  completeFormVerticalTitle,
  initialField,
  initialFieldValue,
  prospectTypeKey,
  fromInviter,
}) {
  return (
    <DrawerPortal>
      <Drawer
        width="100%"
        id="complete-form-drawer"
        direction="bottom"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
      >
        {({ DrawerContent, DrawerCloseButton }) => (
          <DrawerContent>
            <Box position="relative">
              <Box position="absolute" padding="4x" right="0" zIndex="2">
                <DrawerCloseButton onClick={handleDrawerClose} />
              </Box>
              {/* TODO: Write a test case for this */}
              {isDrawerOpen && (
              <AsyncCompleteFormVertical
                formTitle={completeFormTitle}
                formVerticalTitle={completeFormVerticalTitle}
                initialField={initialField}
                initialValues={{ [initialField]: initialFieldValue }}
                initialTouched={{ [initialField]: true }}
                prospectTypeKey={prospectTypeKey}
                fromInviter={fromInviter}
              />
              )}
            </Box>
          </DrawerContent>
        )}
      </Drawer>
    </DrawerPortal>

  );
}

function ShortForm({
  formTitle,
  formSubtitle,
  completeFormTitle,
  completeFormVerticalTitle,
  initialField,
  className,
  prospectTypeKey,
  fromInviter,
  hasTitle,
  formTitleValues,
  shouldShowFormCopy,
  renderDrawerPortal,
}) {
  const { formatMessage } = useIntl();
  const uppercaseInitialField = initialField.toUpperCase();
  const fieldProps = getFieldProperties(initialField);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [initialFieldValue, setInitialFieldValue] = useState('');
  const prospectType = getProspectType(prospectTypeKey);

  const handleSubmit = ({ values, setSubmitting }) => {
    trackShortFormSubmission({ prospectType: prospectType.type });
    setInitialFieldValue(values[initialField]);
    setDrawerOpen(true);
    setSubmitting(false);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleOnBlur = event => {
    if (event.target.value) {
      trackFieldFilling({
        prospectType: prospectType.type,
        field: `${initialField}_short`,
      });
    }
  };

  return (
    <>
      {renderDrawerPortal({
        isDrawerOpen,
        handleDrawerClose,
        completeFormTitle,
        completeFormVerticalTitle,
        initialField,
        initialFieldValue,
        prospectTypeKey,
        fromInviter,
      })}
      <NuDSFormBR>
        <NuDSFormBR.Step
          noValidate
          onSubmit={handleSubmit}
          initialValues={{ [initialField]: '' }}
        >
          {({
            isDisabled,
            isSubmitting,
          }) => (
            <FormContent className={className} id="short-form">
              {hasTitle ? (
                <>
                  {shouldShowFormCopy ? (
                    <Typography
                      variant={formSubtitle ? 'title' : 'subtitle'}
                      tag="h3"
                      color="black.default"
                      marginBottom={formSubtitle ? '4x' : '8x'}
                      intlKey={formTitle}
                      intlValues={formTitleValues}
                    />
                  ) : (
                    <SkeletonForTypography
                      $variant={formSubtitle ? 'title' : 'subtitle'}
                      $highlightColor="white"
                      marginBottom={formSubtitle ? '4x' : '8x'}
                    />
                  )}
                </>
              ) : undefined}

              {formSubtitle && (
                <Typography
                  variant="paragraph2"
                  tag="h3"
                  color="black.default"
                  colorVariant="default"
                  marginBottom="2x"
                  intlKey={formSubtitle}
                  whiteSpace="pre-line"
                />
              )}

              <TextField
                id={`field-${initialField}`}
                name={initialField}
                type={fieldProps.type}
                label={formatMessage({ id: `COMMON.APPLICATION.LONG_LABEL.${uppercaseInitialField}` })}
                syncValidations={normalizeFieldValidations(fieldProps.validations, formatMessage)}
                onBlur={handleOnBlur}
              />

              <Button
                type="submit"
                variant="contained"
                styleVariant="primary"
                extended
                disabled={isDisabled || isSubmitting}
                intlKey="COMMON.WIDGET.SHORT_FORM.CONTINUE_BUTTON"
                iconProps={{ name: 'arrow-right' }}
              />
            </FormContent>
          )}
        </NuDSFormBR.Step>
      </NuDSFormBR>
    </>
  );
}

ShortForm.defaultProps = {
  className: '',
  hasTitle: true,
  formSubtitle: undefined,
  formTitle: 'SHORT_FORM.MULTI_PRODUCT.FORM_TITLE',
  formTitleValues: {},
  fromInviter: false,
  completeFormTitle: 'SHORT_FORM.MULTI_PRODUCT.COMPLETE_FORM_TITLE',
  completeFormVerticalTitle: 'SHORT_FORM.MULTI_PRODUCT.COMPLETE_FORM_VERTICAL_TITLE',
  initialField: 'cpf',
  prospectTypeKey: MULTI_PRODUCT,
  renderDrawerPortal: renderDrawerPortalComponent,
  shouldShowFormCopy: true,
};

ShortForm.propTypes = {
  className: PropTypes.string,
  completeFormTitle: PropTypes.string,
  completeFormVerticalTitle: PropTypes.string,
  formSubtitle: PropTypes.string,
  formTitle: PropTypes.string,
  formTitleValues: PropTypes.objectOf(PropTypes.string),
  fromInviter: PropTypes.bool,
  hasTitle: PropTypes.bool,
  initialField: PropTypes.oneOf(['email', 'name', 'cpf']),
  prospectTypeKey: PropTypes.string,
  renderDrawerPortal: PropTypes.func,
  shouldShowFormCopy: PropTypes.bool,
};

export default React.memo(ShortForm);
